import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

// Public tokenized response page — reached WITHOUT a login (GAL, opposing counsel,
// witness). Reads the request by public_id + token, shows the firm's real open
// phone slots (same engine clients use), and books through book_appointment.
// Plain fetch (no auth client) so it works for a signed-out browser.
const api = (path, opts) => fetch(`/api/appointments/respond${path}`, {
  headers: { 'Content-Type': 'application/json' }, ...opts,
}).then(async (r) => {
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw { status: r.status, detail: body.detail || 'Something went wrong.' };
  return body;
});

export default function AppointmentRespond() {
  const { publicId } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get('t') || '';

  const [details, setDetails] = useState(null);
  const [slots, setSlots] = useState(null);
  const [chosen, setChosen] = useState(null); // {start,end,label}
  const [phase, setPhase] = useState('loading'); // loading|ready|submitting|confirmed|declined|error|declining
  const [err, setErr] = useState('');
  const [declineReason, setDeclineReason] = useState('');

  const loadSlots = useCallback(() => {
    api(`/${publicId}/slots?t=${encodeURIComponent(token)}`)
      .then((d) => setSlots(d.slots || []))
      .catch(() => setSlots([]));
  }, [publicId, token]);

  useEffect(() => {
    api(`/${publicId}?t=${encodeURIComponent(token)}`)
      .then((d) => { setDetails(d); setPhase('ready'); if (d.open_availability) loadSlots(); })
      .catch((e) => { setErr(e.detail || 'This link is not valid.'); setPhase('error'); });
  }, [publicId, token, loadSlots]);

  const accept = async () => {
    if (!chosen) return;
    setPhase('submitting'); setErr('');
    try {
      await api(`/${publicId}/accept`, { method: 'POST', body: JSON.stringify({ token, start: chosen.start, end: chosen.end }) });
      setPhase('confirmed');
    } catch (e) {
      if (e.status === 409) { setErr('That time was just taken — please pick another.'); setPhase('ready'); loadSlots(); }
      else if (e.status === 410) { setErr('This request has already been answered.'); setPhase('error'); }
      else { setErr(e.detail || 'Could not book that time.'); setPhase('ready'); }
    }
  };

  const decline = async () => {
    setPhase('submitting'); setErr('');
    try {
      await api(`/${publicId}/decline`, { method: 'POST', body: JSON.stringify({ token, reason: declineReason || null }) });
      setPhase('declined');
    } catch (e) { setErr(e.detail || 'Could not record that.'); setPhase('ready'); }
  };

  const wrap = (inner) => (
    <div style={{ minHeight: '100vh', background: '#F3EFE7', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, fontFamily: 'Arial, sans-serif' }}>
      <div style={{ maxWidth: 460, width: '100%', background: '#fff', borderRadius: 16, boxShadow: '0 8px 30px rgba(0,0,0,.08)', padding: 28 }}>{inner}</div>
    </div>
  );

  if (phase === 'loading') return wrap(<p style={{ color: '#555' }}>Loading…</p>);
  if (phase === 'error') return wrap(<div><h2 style={{ marginTop: 0 }}>Unavailable</h2><p style={{ color: '#555' }}>{err}</p></div>);
  if (phase === 'confirmed') return wrap(<div><h2 style={{ marginTop: 0, color: '#0f766e' }}>You're booked ✓</h2><p style={{ color: '#444' }}>Your phone appointment with {details.firm_name} is confirmed. A confirmation has been emailed to you.</p></div>);
  if (phase === 'declined') return wrap(<div><h2 style={{ marginTop: 0 }}>Thanks for letting us know</h2><p style={{ color: '#444' }}>We've told {details?.firm_name || 'the firm'} this time doesn't work.</p></div>);

  const proposed = details.proposed_slots && details.proposed_slots.length
    ? details.proposed_slots.map((s, i) => ({ start: s.start, end: s.end, label: (details.proposed_display || [])[i] || `${s.start}` }))
    : null;
  const openSlots = (slots || []).map((s) => ({ start: s.start_time, end: s.end_time, label: `${s.local_time}`, date: s.date }));
  const byDate = openSlots.reduce((a, s) => { (a[s.date] = a[s.date] || []).push(s); return a; }, {});

  return wrap(
    <div>
      <h2 style={{ marginTop: 0, marginBottom: 6 }}>Appointment with {details.firm_name}</h2>
      <p style={{ color: '#444', marginTop: 0 }}>
        {details.attorney_name} would like to schedule a <strong>phone appointment</strong>
        {details.recipient_name ? ` with you, ${details.recipient_name}` : ''}. Pick a time below.
      </p>
      <p style={{ color: '#888', fontSize: 12 }}>Times shown in {details.timezone}.</p>
      {details.consult_fee_cents > 0 && (
        <p style={{ color: '#8a6d1a', background: '#fdf6e3', border: '1px solid #e8d9a0',
                    borderRadius: 6, padding: '8px 10px', fontSize: 13 }}>
          A consultation fee of <strong>{details.consult_fee_display}</strong> applies to this
          appointment. Payment details will follow once your time is confirmed.
        </p>
      )}

      {proposed ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '14px 0' }}>
          {proposed.map((s) => (
            <button key={s.start} onClick={() => setChosen(s)} style={btn(chosen?.start === s.start)}>{s.label}</button>
          ))}
        </div>
      ) : (
        <div style={{ maxHeight: 320, overflowY: 'auto', margin: '14px 0' }}>
          {slots === null && <p style={{ color: '#888' }}>Loading available times…</p>}
          {slots && slots.length === 0 && <p style={{ color: '#888' }}>No open times right now — please reply to the email.</p>}
          {Object.entries(byDate).map(([d, list]) => (
            <div key={d} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>{new Date(d + 'T12:00:00').toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {list.map((s) => <button key={s.start} onClick={() => setChosen(s)} style={btn(chosen?.start === s.start)}>{s.label}</button>)}
              </div>
            </div>
          ))}
        </div>
      )}

      {err && <p style={{ color: '#b91c1c', fontSize: 14 }}>{err}</p>}
      <button onClick={accept} disabled={!chosen || phase === 'submitting'} style={{ ...primaryBtn, opacity: (!chosen || phase === 'submitting') ? 0.5 : 1 }}>
        {chosen ? `Confirm ${chosen.label}` : 'Choose a time'}
      </button>

      <details style={{ marginTop: 16 }}>
        <summary style={{ color: '#888', fontSize: 13, cursor: 'pointer' }}>None of these work?</summary>
        <div style={{ marginTop: 8 }}>
          <input value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} placeholder="(optional) a note back to the firm" style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 8, marginBottom: 8, boxSizing: 'border-box' }} />
          <button onClick={decline} disabled={phase === 'submitting'} style={{ ...secondaryBtn }}>Decline these times</button>
        </div>
      </details>
    </div>
  );
}

const btn = (active) => ({ padding: '8px 12px', borderRadius: 8, border: active ? '2px solid #0d9488' : '1px solid #ddd', background: active ? '#f0fdfa' : '#fff', color: '#222', cursor: 'pointer', fontSize: 14 });
const primaryBtn = { width: '100%', padding: '12px', borderRadius: 10, border: 'none', background: '#0d9488', color: '#fff', fontSize: 15, cursor: 'pointer', marginTop: 4 };
const secondaryBtn = { padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', color: '#555', cursor: 'pointer', fontSize: 14 };
